import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Account schema (šema) row. Mirrors ChartOfAccountsModel: `schema` is the
 * code (like `konto`) and `nazivSheme` the display name (like `nazivKonta`).
 */
export interface AccountSchemaModel {
  id?: string;
  schema: string;
  nazivSheme: string;
  selected?: boolean;
  clientID?: string;
  searchKeyword?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountSchemaDataService {
  constructor(private snackBar: MatSnackBar) {}

  private SCHEMA_DATA = new BehaviorSubject<AccountSchemaModel[]>([]);
  private dataStore: { schemas: AccountSchemaModel[] } = { schemas: [] };
  readonly schemas = this.SCHEMA_DATA.asObservable();
  private nextToken: string | undefined;

  resetData() {
    this.dataStore.schemas = [];
    this.SCHEMA_DATA.next([]);
  }

  async listByClientID(
    clientID: string,
    filter?: any,
    limit?: number,
    nextToken?: string
  ): Promise<{ items: AccountSchemaModel[]; nextToken: string | null }> {
    const statement = `query GetAccountSchemasByClientID($clientID: ID!, $filter: ModelAccountSchemaFilterInput, $limit: Int, $nextToken: String) {
      getAccountSchemasByClientID(clientID: $clientID, filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {
          id
          schema
          nazivSheme
          clientID
          createdAt
          updatedAt
        }
        nextToken
      }
    }`;
    const gqlAPIServiceArguments: any = {
      clientID,
      limit: limit || 50,
    };
    if (filter) gqlAPIServiceArguments.filter = filter;
    if (nextToken) gqlAPIServiceArguments.nextToken = nextToken;

    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return response.data.getAccountSchemasByClientID;
  }

  async listAllByClientID(clientID: string): Promise<AccountSchemaModel[]> {
    let allItems: AccountSchemaModel[] = [];
    let token: string | null = null;
    do {
      const result: any = await this.listByClientID(clientID, undefined, 10000, token || undefined);
      allItems = allItems.concat(result.items);
      token = result.nextToken;
    } while (token);
    return allItems;
  }

  async createAccountSchema(input: AccountSchemaModel): Promise<AccountSchemaModel> {
    const statement = `mutation CreateAccountSchema($input: CreateAccountSchemaInput!) {
      createAccountSchema(input: $input) {
        id
        schema
        nazivSheme
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.createAccountSchema;
  }

  async updateAccountSchema(input: Partial<AccountSchemaModel> & { id: string }): Promise<AccountSchemaModel> {
    const statement = `mutation UpdateAccountSchema($input: UpdateAccountSchemaInput!) {
      updateAccountSchema(input: $input) {
        id
        schema
        nazivSheme
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.updateAccountSchema;
  }

  async deleteAccountSchema(id: string): Promise<void> {
    const statement = `mutation DeleteAccountSchema($input: DeleteAccountSchemaInput!) {
      deleteAccountSchema(input: $input) {
        id
      }
    }`;
    await API.graphql(graphqlOperation(statement, { input: { id } }));
  }

  async bulkImport(csvUrl: string): Promise<any> {
    const statement = `mutation BulkAccountSchemaImport($input: ModelBulkAccountSchemaImportInput!) {
      bulkAccountSchemaImport(input: $input) {
        result
        message
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input: { csvUrl } })
    )) as any;
    return response?.data?.bulkAccountSchemaImport ?? { result: 'SUCCESS' };
  }

  async initializeData(clientID: string): Promise<void> {
    const data = await this.listByClientID(clientID, undefined, 50)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.schemas = data.items;
    this.SCHEMA_DATA.next([...this.dataStore.schemas]);
    if ((data as any).nextToken) {
      this.nextToken = (data as any).nextToken;
    } else {
      this.nextToken = undefined;
    }
  }

  async filterSchemas(clientID: string, keyword: string): Promise<void> {
    this.dataStore.schemas = [];
    let token: string | null = null;
    do {
      const data: any = await this.listByClientID(
        clientID,
        { searchKeyword: { contains: keyword } },
        100000,
        token || undefined
      ).catch((err) => { console.log(err); return null; });
      if (data) {
        this.dataStore.schemas = [...this.dataStore.schemas, ...data.items];
        this.SCHEMA_DATA.next([...this.dataStore.schemas]);
        token = data.nextToken;
      } else {
        token = null;
      }
    } while (token);
  }

  async fetchAdditional(clientID: string): Promise<void> {
    if (!this.nextToken) return;
    const data = await this.listByClientID(clientID, undefined, 150, this.nextToken)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.schemas = this.dataStore.schemas.concat(data.items);
    this.SCHEMA_DATA.next([...this.dataStore.schemas]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  private buildSearchKeyword(schema: AccountSchemaModel): string {
    return `${schema.schema}${schema.nazivSheme}`.toLowerCase().replace(/\s/g, '');
  }

  async addSchema(schema: AccountSchemaModel): Promise<void> {
    const user = await Auth.currentAuthenticatedUser();
    const input: AccountSchemaModel = {
      clientID: user?.attributes.sub,
      schema: schema.schema,
      nazivSheme: schema.nazivSheme,
      searchKeyword: this.buildSearchKeyword(schema),
    };
    const created = await this.createAccountSchema(input);
    this.dataStore.schemas.unshift(created);
    this.SCHEMA_DATA.next([...this.dataStore.schemas]);
  }

  async editSchema(schemaData: AccountSchemaModel): Promise<void> {
    const found = this.dataStore.schemas.find((s) => s.id === schemaData.id);
    if (found) {
      const updated = await this.updateAccountSchema({
        id: schemaData.id!,
        schema: schemaData.schema,
        nazivSheme: schemaData.nazivSheme,
        searchKeyword: this.buildSearchKeyword(schemaData),
      });
      Object.assign(found, updated);
      this.SCHEMA_DATA.next([...this.dataStore.schemas]);
      this.snackBar.open('Changes successfully saved', '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }

  async removeSchema(item: AccountSchemaModel): Promise<void> {
    const found = this.dataStore.schemas.find((s) => s.id === item.id);
    if (found) {
      this.dataStore.schemas = this.dataStore.schemas.filter((s) => s.id !== item.id);
      this.SCHEMA_DATA.next([...this.dataStore.schemas]);
      await this.deleteAccountSchema(item.id!).catch((err) => console.log(err));
      this.snackBar.open(`The "${item.schema}" schema has been deleted`, '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }
}
